import { format, formatISO, parse, parseISO } from 'date-fns';
import z from 'zod';

export const isoDateOptionalTimeRegex = /^\d\d\d\d-\d\d-\d\d(?:T\d\d:\d\d(?::\d\d(?:\.\d{1,3})?)?)?Z?$/;
export const isoDateTimeRegex = /^\d\d\d\d-\d\d-\d\dT\d\d:\d\d(?::\d\d(?:\.\d{1,3})?)?Z?$/;

export function isZodLiteralValue(value: unknown): value is z.util.Literal {
    return (
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean' ||
        typeof value === 'bigint' ||
        value === null ||
        value === undefined
    );
}

/**
 * Defines a codec which maps one value to another.
 */
export function mapValue<Input extends string, Output>(
    map: Record<Input, Output> | [Input, Output][],
): z.ZodCodec<z.ZodType<Input, Input>, z.ZodType<Output, Output>> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const mapArray: [Input, Output][] = Array.isArray(map) ? map : (Object.entries(map) as [Input, Output][]);

    return z.codec(
        z.custom<Input>((value) => mapArray.some(([input]) => value === input)),
        z.custom<Output>((value) => mapArray.some(([, output]) => value === output)),
        {
            decode: (input): Output => {
                const output = mapArray.find(([inp]) => inp === input)?.[1];
                if (output === undefined) {
                    // the types of the function should stop us getting here
                    throw new TypeError(`input could not be mapped to output`);
                }
                return output;
            },
            encode: (output): Input => {
                const input = mapArray.find(([, out]) => out === output)?.[0];
                if (input === undefined) {
                    // the types of the function should stop us getting here
                    throw new TypeError(`output could not be mapped to input`);
                }
                return input;
            },
        },
    );
}

/**
 * Wraps a schema to normalize input values of `""` (or whitespace-only),
 * `null` and `undefined` to `undefined`.
 */
export function maybe<Output, Input>(
    schema: z.core.$ZodType<Output, Input>,
): z.ZodOptional<z.ZodType<Output | undefined, Input | '' | null | undefined>> {
    // z.optional isn't required here for the logic, but we have it so that the
    // the type inference infers that the property is optional
    return z.optional(
        z.codec(z.union([z.literal(['', null, undefined]), z.custom<Input>()]), z.optional(schema), {
            decode: (value): Input | undefined => {
                if (value === null || value === '' || (typeof value === 'string' && value.trim() === '')) {
                    return undefined;
                }
                return value;
            },
            encode: (value): Input | undefined => {
                return value;
            },
        }),
    );
}

export function normalizeIsoDateString() {
    return z
        .string()
        .regex(isoDateOptionalTimeRegex)
        .transform((value) => value.slice(0, 10));
}

export type IsoDateTimeOptions = {
    error?: string;
    microseconds?: boolean;
    zulu?: boolean;
};

export function normalizeIsoDateTimeString(options: IsoDateTimeOptions = {}) {
    const { error = 'expected ISO date time', microseconds = true, zulu = true } = options;
    return z
        .string()
        .regex(isoDateTimeRegex, { error })
        .transform((value, ctx) => {
            const date = new Date(value);
            if (Number.isNaN(date.getTime())) {
                ctx.addIssue(error);
                return z.NEVER;
            }

            let str = date.toISOString();
            if (!microseconds) {
                str = str.slice(0, -5) + 'Z';
            }
            if (!zulu) {
                str = str.slice(0, -1);
            }
            return str;
        });
}

/**
 * Parses a string date in the given format.
 */
export function convertDateString(encodedFormat: string, decodedFormat?: string): z.ZodType<string, string> {
    function transform(from?: string, to?: string) {
        return (value: string, payload: z.core.ParsePayload<string>): string => {
            let dateValue: Date;
            if (from) {
                dateValue = parse(value, from, new Date());
            } else {
                dateValue = parseISO(value);
            }

            if (Number.isNaN(dateValue.getTime())) {
                payload.issues.push({
                    code: 'invalid_format',
                    format: 'datetime',
                    input: value,
                    continue: false,
                    message: 'expected date format',
                });

                return z.NEVER;
            }

            if (to) {
                return format(dateValue, to);
            }
            return formatISO(dateValue, { representation: 'date' });
        };
    }

    return z.codec(z.string(), z.string(), {
        decode: transform(encodedFormat, decodedFormat),
        encode: transform(decodedFormat, encodedFormat),
    });
}

export function stringToArray(delimiter?: string): z.ZodCodec<z.ZodString, z.ZodArray<z.ZodString>>;
export function stringToArray<Element extends z.core.$ZodType<any, string>>(
    delimiter: string | undefined,
    element: Element,
): z.ZodCodec<z.ZodString, z.ZodArray<Element>>;
export function stringToArray(
    delimiter = ',',
    element: z.core.$ZodType<any, string> = z.string().refine((element) => !element.includes(delimiter)),
): z.ZodCodec<z.ZodString, z.ZodArray<z.core.$ZodType<any, string>>> {
    return z.codec(z.string(), z.array(element), {
        decode: (value) => value.split(delimiter),
        encode: (value) => value.join(delimiter),
    });
}

export type StringToBoolOptions = {
    trim?: boolean;
};

/**
 * Converts string representations of boolean to JavaScript `boolean` type.
 */
export function stringToBool(): z.ZodCodec<z.ZodType<'true' | 'false', string>, z.ZodBoolean>;
export function stringToBool<False extends string, True extends string>(
    falseValue: False | False[],
    trueValue: True | True[],
    options?: StringToBoolOptions,
): z.ZodCodec<z.ZodType<False | True, string>, z.ZodBoolean>;
export function stringToBool(
    falseValue: string | string[] = 'false',
    trueValue: string | string[] = 'true',
    options?: StringToBoolOptions,
): z.ZodCodec<z.ZodType<string>, z.ZodBoolean> {
    const falseValues = Array.isArray(falseValue) ? falseValue : [falseValue];
    const trueValues = Array.isArray(trueValue) ? trueValue : [trueValue];
    const allValues = [...falseValues, ...trueValues];

    const primaryFalseValue = falseValues[0];
    const primaryTrueValue = trueValues[0];

    if (!primaryFalseValue || !primaryTrueValue) {
        throw new TypeError(`must provide at least one true value and at least one false value`);
    }

    return z.codec(
        z.custom<string>((value) => {
            if (options?.trim && typeof value === 'string') {
                value = value.trim();
            }
            return allValues.includes(value as string);
        }),
        z.boolean(),
        {
            decode: (value) => {
                if (options?.trim) {
                    value = value.trim();
                }
                return trueValues.includes(value);
            },
            encode: (value) => (value ? primaryTrueValue : primaryFalseValue),
        },
    );
}

/**
 * Converts string representations of integers to JavaScript `number` type using
 * `parseInt()`.
 */
export function stringToInt() {
    return z.codec(z.string().regex(/^-?\d+$/), z.int(), {
        decode: (str) => Number.parseInt(str, 10),
        encode: (value) => value.toFixed(0),
    });
}

export function stringToUnsignedInt() {
    return z.codec(z.string().regex(/^\d+$/), z.int(), {
        decode: (str) => Number.parseInt(str, 10),
        encode: (value) => value.toFixed(0),
    });
}

/**
 * Converts string representations of integers to JavaScript `number` type using
 * `parseInt()`.
 */
export function stringToNumber() {
    return z.codec(z.string().regex(z.regexes.number), z.number(), {
        decode: (str) => Number.parseFloat(str),
        encode: String,
    });
}

/**
 * Parses JSON strings into structured data and serializes back to JSON. Accepts
 * an output schema to validate the parsed JSON data.
 */
export function json<T extends z.core.$ZodType>(output: T): z.ZodCodec<z.ZodString, T> {
    return z.codec(z.string(), output, {
        decode: (value, ctx) => {
            try {
                /* eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion 
                    -- this gets picked up by the output validator in the codec */
                return JSON.parse(value) as z.input<T>;
            } catch (error) {
                if (error instanceof SyntaxError) {
                    ctx.issues.push({
                        code: 'invalid_format',
                        format: 'json',
                        input: value,
                        message: error.message,
                    });
                    return z.NEVER;
                }
                throw error;
            }
        },

        encode: (value) => JSON.stringify(value),
    });
}

/**
 * Validates JSON strings but leaves as JSON. See {@link json} to return the
 * parsed object as well as validating.
 */
export function jsonString(output: z.core.$ZodType): z.ZodString {
    return z.string().superRefine((value, ctx) => {
        let parsed: unknown;
        try {
            parsed = JSON.parse(value);
        } catch (error) {
            if (error instanceof SyntaxError) {
                ctx.issues.push({
                    code: 'invalid_format',
                    format: 'json',
                    input: value,
                    message: error.message,
                });
                return;
            }
            throw error;
        }

        const result = z.safeParse(output, parsed);
        if (!result.success) {
            for (const issue of result.error.issues) {
                const path = issue.path
                    .map((x) => (typeof x === 'number' ? `${x}` : `"${String(x)}"`))
                    .join('.');

                ctx.addIssue({
                    code: 'custom',
                    message: `${path}: ${issue.message}`,
                });
            }
        }
    });
}

export const isoDatetimeToDate = z.codec(z.string(), z.date(), {
    decode: (value) => new Date(value),
    encode: (value) => value.toISOString(),
});
